import axios from 'axios';
import qs from 'query-string';

/**
 * 客户信息接口定义
 * 用于描述客户的基本信息
 */
export interface CustomerRecord {
  /** 客户头像 */
  avatar: string;
  /** 城市 */
  city: string;
  /** 用户ID */
  user_id: number;
  /** 客户昵称 */
  nickname: string;
  /** 客户手机号 */
  mobile: string;
  /** 最后消费时间 */
  last_consume_time: string | null;
  /** 最后报备时间 */
  last_report_time: string;
  /** 下次跟进时间 */
  next_contact_time: string;
  /** 归属用户姓名 */
  master_nickname: string;
  /** 归属用户手机号 */
  master_mobile: string;
  /** 归属用户团队领导 */
  master_group: string;
  /** 是否今日联系, 1是 0否 */
  is_today_contact: number;
  reminder_id?: number;
}

/**
 * 客户查询参数接口
 * 继承自CustomerRecord，用于查询筛选
 */
export interface CustomerParams extends Partial<CustomerRecord> {
  /** 客户昵称（模糊查询） */
  nickname?: string;
  /** 当前页码，必填 */
  page: number;
  /** 每页数量，可选 */
  pageSize?: number;
  /** 客户类型, 枚举值self自己客户 distribute辅助客户 */
  type: string;
  /** 查看全部客户，1是 0否 */
  all?: number;
}

/**
 * 跟进列表响应数据接口
 */
export interface CustomerListRes {
  /** 跟进记录列表 */
  list: CustomerRecord[];
  /** 总记录数 */
  total: number;
}

/**
 * 查询跟进列表
 * @param params 查询参数，包含筛选条件和分页信息
 * @returns Promise<CustomerListRes> 返回跟进列表数据
 */
export function queryCustomerList(params: CustomerParams) {
  return axios.get<CustomerListRes>('/business/customer_list', {
    params,
    paramsSerializer: (obj) => qs.stringify(obj),
  });
}

/**
 * 跟进计划记录接口定义
 * 用于描述跟进计划的基本信息
 */
export interface FollowUpScheduleRecord {
  /** 计划记录唯一标识ID */
  id: number;
  /** 用户ID */
  user_id: number;
  /** 客户ID */
  customer_id: number;
  /** 员工ID */
  staff_id: number;
  /** 跟进内容 */
  content: string;
  /** 创建时间戳 */
  createtime: number;
  /** 更新时间戳 */
  updatetime: number;
}

/**
 * 跟进计划查询参数接口
 * 继承自FollowUpScheduleRecord，用于查询筛选
 */
export interface FollowUpScheduleParams {
  /** 当前页码，必填 */
  page: number;
  /** 每页数量，可选 */
  pageSize?: number;
  /** 客户ID */
  user_id?: number;
  /** 回访结果,枚举值1、2、3、4、-1、-2，0或空全部，对应关系：1-有效一级，2-有效二级，3-有效三级，4-有效四级，-1-无效一级，-2-无效二级 */
  result_level?: number | string;
  /** 关键词 */
  keyword?: string;
  /** 日期 */
  date?: string;
  /** 回访人 */
  staff_name?: string;
}

/**
 * 跟进计划列表响应数据接口
 */
export interface FollowUpScheduleListRes {
  /** 跟进计划记录列表 */
  list: FollowUpScheduleRecord[];
  /** 总记录数 */
  total: number;
}

/**
 * 添加回访计划参数接口
 */
export interface AddFollowUpScheduleParams {
  /** 客户ID */
  user_id: number;
  /** 员工ID */
  remind_time: string;
  /** 跟进内容 */
  content: string;
  /** 回访结果 */
  result_level?: number | string;
  /** 日期范围 */
  date?: string[];
  /** 回访人 */
  staff_name?: string;
  /** 关键词 */
  keyword?: string;
}

/**
 * 查询回访记录列表
 * @param params 查询参数，包含筛选条件和分页信息
 * @returns Promise<FollowUpScheduleListRes> 返回跟进计划列表数据
 */
export function queryFollowUpScheduleList(params: FollowUpScheduleParams) {
  return axios.get<FollowUpScheduleListRes>('/business/contact_log_list', {
    params,
    paramsSerializer: (obj) => qs.stringify(obj),
  });
}

/**
 * 增加回访计划
 * @param params 添加参数，包含客户ID、提醒时间、提醒内容
 * @returns Promise<void>
 */
export function addFollowUpSchedule(params: AddFollowUpScheduleParams) {
  return axios.post('/business/set_reminder', params);
}

/**
 * 添加回访记录参数接口
 */
export interface AddFollowUpParams {
  /** 回访记录ID */
  id: number;
  /** 回访结果 */
  result_level: number | string;
  /** 回访内容 */
  content: string;
  /** 回访计划ID */
  reminder_id: number;
}

/**
 * 增加回访记录
 * @param params 添加参数，包含回访记录ID、回访结果、回访内容、回访计划ID
 * @returns Promise<void>
 */
export function addFollowUp(params: AddFollowUpParams) {
  return axios.post('/business/contact_log_save', params);
}

/**
 * 更新回访记录
 * @param params 更新参数，包含回访记录ID、回访结果、回访内容
 * @returns Promise<void>
 */
export function updateFollowUp(params: Partial<AddFollowUpParams>) {
  return axios.post('/business/contact_log_save', params);
}

/**
 * 删除回访记录
 * @param id 回访记录ID
 * @returns Promise<void>
 */
export function deleteFollowUp(id: number) {
  return axios.post(`/business/contact_log_del`, { id });
}

/**
 * 今日回访记录接口
 */
export interface TodayFollowUpRecord {
  city: string;
  content: string;
  mobile: string;
  nickname: string;
  remind_time: string;
  staff_id: number;
  status: number;
  user_id: number;
}

/**
 * 今日回访参数接口
 */
export interface TodayFollowUpParams {
  page: number;
  pageSize: number;
  expired?: number;
}

export interface TodayFollowUpListRes {
  /** 跟进记录列表 */
  data: TodayFollowUpRecord[];
  /** 总记录数 */
  total: number;
}

/**
 * 今日回访列表
 * @param params 查询参数，包含筛选条件和分页信息
 * @returns Promise<TodayFollowUpListRes> 返回今日回访列表数据
 */
export function queryTodayFollowUpList(params: TodayFollowUpParams) {
  return axios.get<TodayFollowUpListRes>('/tool/today_customers_plan', {
    params,
    paramsSerializer: (obj) => qs.stringify(obj),
  });
}

/**
 * 回访记录筛选参数
 * @param params 筛选参数，包含电话号码、姓名、日期范围、回访人
 * @param params.keyword 关键词
 * @param params.data_range 日期范围
 * @param params.staff_name 回访人
 */
export interface FollowUpRecordFilterParams {
  keyword: string;
  date: string;
  staff_name: string;
  result_level: string;
}

/**
 * 回访记录信息接口
 */
export interface FollowUpRecordInfo {
  reminder_id: number;
  user_id: number;
  content: string;
  result_level: number | string;
  createtime: number;
  updatetime: number;
}

/**
 * 回访记录列表请求参数
 */
export interface FollowUpRecordListParams extends FollowUpRecordFilterParams {
  page: number;
  pageSize: number;
}

/**
 * 回访记录列表响应数据
 */
export interface FollowUpRecordListRes {
  list: FollowUpRecordInfo[];
  total: number;
}

/**
 * 查询回访记录列表
 * @param params 查询参数，包含筛选条件和分页信息
 * @returns Promise<FollowUpRecordListRes> 返回回访记录列表数据
 */
export function queryFollowUpRecordList(params: FollowUpRecordListParams) {
  return axios.get<FollowUpRecordListRes>('/business/contact_log_list', {
    params,
    paramsSerializer: (obj) => qs.stringify(obj),
  });
}

/**
 * 查询所有员工回访记录列表
 */
export function queryAllStaffFollowUpRecordList(
  params: FollowUpRecordListParams
) {
  return axios.get<FollowUpRecordListRes>('/business/contact_log_all', {
    params,
    paramsSerializer: (obj) => qs.stringify(obj),
  });
}

/**
 * 回访计划列表记录接口
 */
export interface FollowUpPlanRecord {
  id: number;
  user_id: number;
  remind_time: string;
  status: number;
  createtime: number;
  updatetime: number;
  nickname: string;
  mobile: string;
  staff_name?: string;
  type?: string;
  content?: string;
  log: {
    id: number;
    staff_id: number;
    user_id: number;
    reminder_id: number;
    result_level: number;
    content: string;
    createtime: string;
  }[];
}

/**
 * 回访计划列表请求参数
 */
export interface FollowUpPlanListParams {
  user_id?: number;
  page: number;
  pageSize: number;
}

export interface FollowUpPlanListRes {
  list: FollowUpPlanRecord[];
  total: number;
}

/**
 * 查询回访计划列表
 * @param params 查询参数，包含筛选条件和分页信息
 * @returns Promise<FollowUpPlanListRes> 返回回访计划列表数据
 */
export function queryFollowUpPlanList(params: FollowUpPlanListParams) {
  return axios.get<FollowUpPlanListRes>('/business/reminder_list', {
    params,
    paramsSerializer: (obj) => qs.stringify(obj),
  });
}

/**
 * 删除回访计划
 * @param id 回访计划ID
 * @returns Promise<void>
 */
export function deleteFollowUpPlan(id: number) {
  return axios.post(`/business/reminder_del`, { id });
}
